import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/ui/dashboard-layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { FormField, useFieldValidation, validators } from '@/components/ui/form-field';
import { Separator } from '@/components/ui/separator';
import { useAuthStore } from '@/stores/auth-store';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { toast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api';
import { Link } from 'react-router-dom';
import { useNotificationPreferences, useUpdateNotificationPreferences } from '@/hooks/use-notifications';
import { Switch } from '@/components/ui/switch';
import { useTranslation } from 'react-i18next';
import { Loader2, Save, Shield, User, KeyRound, Download, Trash2, AlertTriangle, Bell } from 'lucide-react';
import { PasswordStrengthMeter } from '@/components/ui/password-strength';
import { TwoFactorSetup } from '@/components/auth/two-factor-setup';

export function ProfilePage() {
  const { t } = useTranslation();
  useDocumentTitle(t('profile.title'));

  const { user, setUser, logout } = useAuthStore();

  // Profile fields
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [profileSaving, setProfileSaving] = useState(false);

  const currentPwValidation = useFieldValidation([validators.required(t('profile.currentPasswordRequired'))]);
  const newPwValidation = useFieldValidation([validators.required(), validators.minLength(6, t('errors.passwordMinLength'))]);
  const confirmPwValidation = useFieldValidation([
    validators.required(),
    (v: string) => (newPwValidation.value && v !== newPwValidation.value) ? t('errors.passwordsDoNotMatch') : null,
  ]);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // GDPR fields
  const [exporting, setExporting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Sync state when user data changes (e.g. after profile update)
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setPhone(user.phone || '');
    }
  }, [user]);

  async function handleExportData() {
    setExporting(true);
    try {
      const res = await apiClient.get('/auth/export-data');
      if (res.success && res.data) {
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dyndev-data-export-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast({ title: t('profile.dataExported'), description: t('profile.dataExportedDesc'), variant: 'success' });
      }
    } catch (err: any) {
      toast({ title: t('profile.exportFailed'), description: err?.message || t('profile.exportFailedDesc'), variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  }

  const handleDeleteAccount = async () => {
    setDeleteError('');
    if (!deletePassword) {
      setDeleteError(t('profile.deletePasswordRequired'));
      return;
    }
    setDeleting(true);
    try {
      await apiClient.post('/auth/delete-account', { password: deletePassword });
      toast({ title: t('profile.accountDeleted'), description: t('profile.accountDeletedDesc'), variant: 'success' });
      logout();
    } catch (err: any) {
      const message = err?.message || t('profile.failedDelete');
      setDeleteError(message);
      toast({ title: t('profile.deletionFailed'), description: message, variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setProfileSaving(true);

    try {
      const res = await apiClient.put('/auth/profile', {
        name: name.trim(),
        phone: phone.trim() || undefined,
      });

      if (res.success && res.data) {
        setUser(res.data);
        toast({
          title: t('profile.profileUpdated'),
          description: t('profile.profileUpdatedDesc'),
          variant: 'success',
        });
      }
    } catch (err: any) {
      toast({
        title: t('profile.failedUpdateProfile'),
        description: err?.message || t('errors.unexpected'),
        variant: 'destructive',
      });
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (!currentPwValidation.value) {
      setPasswordError(t('profile.currentPasswordRequired'));
      return;
    }
    if (newPwValidation.value.length < 6) {
      setPasswordError(t('errors.passwordMinLength'));
      return;
    }
    if (newPwValidation.value !== confirmPwValidation.value) {
      setPasswordError(t('errors.passwordsDoNotMatch'));
      return;
    }

    setPasswordSaving(true);

    try {
      const res = await apiClient.post('/auth/change-password', {
        currentPassword: currentPwValidation.value,
        newPassword: newPwValidation.value,
      });

      if (res.success) {
        toast({
          title: t('auth.passwordChanged'),
          description: t('auth.passwordChangedDesc'),
          variant: 'success',
        });

        currentPwValidation.reset();
        newPwValidation.reset();
        confirmPwValidation.reset();
      }
    } catch (err: any) {
      const message = err?.message || t('auth.failedChangePassword');
      setPasswordError(message);
      toast({ title: t('auth.failedChangePassword'), description: message, variant: 'destructive' });
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">{t('profile.title')}</h1>

        <div className="space-y-6">
          {/* Profile Info */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <User size={18} className="text-dyndev-green" />
                <CardTitle className="text-lg">{t('profile.profileInfo')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <FormField label={t('profile.fullName')}>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('profile.namePlaceholder')}
                    required
                  />
                </FormField>

                <FormField label={t('profile.email')} helperText={t('profile.emailCannotChange')}>
                  <Input
                    value={user?.email || ''}
                    disabled
                    className="opacity-60 cursor-not-allowed"
                  />
                </FormField>

                <FormField label={t('profile.phone')}>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={t('profile.phonePlaceholder')}
                  />
                </FormField>

                <div className="flex items-center gap-2 pt-1">
                  <div className="text-xs text-[var(--text-secondary)] capitalize flex items-center gap-1">
                    <Shield size={12} />
                    {t('profile.role', { role: user?.role?.replace('_', ' ') || 'N/A' })}
                  </div>
                  <Button
                    type="submit"
                    size="sm"
                    disabled={profileSaving || !name.trim()}
                    className="ml-auto gap-1.5"
                  >
                    {profileSaving ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Save size={14} />
                    )}
                    {profileSaving ? t('common.saving') : t('common.save')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Two-Factor Authentication */}
          <TwoFactorSetup
            enabled={user?.twoFactorEnabled || false}
            onStatusChange={(enabled) => {
              if (user) setUser({ ...user, twoFactorEnabled: enabled });
            }}
          />

          {/* Notification Preferences */}
          <NotificationPreferences />

          {/* GDPR & Privacy */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Shield size={18} className="text-dyndev-green" />
                <CardTitle className="text-lg">{t('profile.privacyData')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]">
                <p className="text-xs text-[var(--text-secondary)] mb-3">
                  {t('profile.gdprDesc')}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={exporting}
                    onClick={handleExportData}
                    className="gap-1.5"
                  >
                    {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                    {exporting ? t('profile.exportingData') : t('profile.exportData')}
                  </Button>
                  <Link
                    to="/privacy-policy"
                    className="text-[10px] text-dyndev-green hover:text-dyndev-green/80 transition-colors"
                  >
                    {t('profile.privacyPolicy')}
                  </Link>
                </div>
              </div>

              {/* Account deletion */}
              {!deleteConfirm ? (
                <div className="p-3 rounded-lg border border-red-500/20 bg-red-500/5">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle size={14} className="text-red-400" />
                    <span className="text-sm font-medium text-red-400">{t('profile.dangerZone')}</span>
                  </div>
                  <p className="text-[10px] text-[var(--text-secondary)] mb-3">
                    {t('profile.dangerZoneDesc')}
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDeleteConfirm(true)}
                    className="gap-1.5 text-red-400 border-red-500/20 hover:bg-red-500/10 hover:text-red-300"
                  >
                    <Trash2 size={12} />
                    {t('profile.deleteAccount')}
                  </Button>
                </div>
              ) : (
                <div className="p-3 rounded-lg border border-red-500/20 bg-red-500/5">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle size={14} className="text-red-400" />
                    <span className="text-sm font-medium text-red-400">{t('profile.deleteConfirmTitle')}</span>
                  </div>
                  <p className="text-[10px] text-[var(--text-secondary)] mb-3">
                    {t('profile.deleteConfirmDesc')}
                  </p>
                  {deleteError && (
                    <div className="p-2 rounded bg-red-500/10 border border-red-500/20 text-red-400 text-[10px] mb-2">
                      {deleteError}
                    </div>
                  )}
                  <Input
                    type="password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    placeholder={t('profile.deleteConfirmPlaceholder')}
                    className="mb-2 text-sm h-8"
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setDeleteConfirm(false); setDeletePassword(''); setDeleteError(''); }}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      size="sm"
                      disabled={deleting || !deletePassword}
                      onClick={handleDeleteAccount}
                      className="bg-red-500/20 text-red-400 border-red-500/30 hover:bg-red-500/30 gap-1.5"
                    >
                      {deleting && <Loader2 size={12} className="animate-spin" />}
                      {deleting ? t('profile.deleting') : t('profile.confirmDelete')}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <KeyRound size={18} className="text-dyndev-green" />
                <CardTitle className="text-lg">{t('profile.changePassword')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                {passwordError && (
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                    {passwordError}
                  </div>
                )}

                <FormField
                  label={t('auth.currentPassword')}
                  error={currentPwValidation.showError ? currentPwValidation.error : null}
                  showError={currentPwValidation.showError}
                  isValid={currentPwValidation.isValid}
                >
                  <Input
                    type="password"
                    value={currentPwValidation.value}
                    onChange={currentPwValidation.onChange}
                    onBlur={currentPwValidation.onBlur}
                    placeholder={t('auth.passwordPlaceholder')}
                  />
                </FormField>

                <Separator className="my-2" />

                <FormField
                  label={t('auth.newPassword')}
                  error={newPwValidation.showError ? newPwValidation.error : null}
                  showError={newPwValidation.showError}
                  isValid={newPwValidation.isValid}
                >
                  <Input
                    type="password"
                    value={newPwValidation.value}
                    onChange={newPwValidation.onChange}
                    onBlur={newPwValidation.onBlur}
                    placeholder={t('auth.passwordPlaceholder')}
                  />
                </FormField>
                <PasswordStrengthMeter password={newPwValidation.value} />

                <FormField
                  label={t('auth.confirmNewPassword')}
                  error={confirmPwValidation.showError ? confirmPwValidation.error : null}
                  showError={confirmPwValidation.showError}
                  isValid={confirmPwValidation.isValid}
                >
                  <Input
                    type="password"
                    value={confirmPwValidation.value}
                    onChange={confirmPwValidation.onChange}
                    onBlur={confirmPwValidation.onBlur}
                    placeholder={t('auth.passwordPlaceholder')}
                  />
                </FormField>

                <Button
                  type="submit"
                  size="sm"
                  disabled={
                    passwordSaving ||
                    !currentPwValidation.value ||
                    !newPwValidation.value ||
                    !confirmPwValidation.value
                  }
                  className="gap-1.5"
                >
                  {passwordSaving && <Loader2 size={14} className="animate-spin" />}
                  {passwordSaving ? t('profile.changing') : t('profile.changePassword')}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}

// ==================================================================
// Notification Preferences component (inline to avoid separate file)
// ==================================================================
const NOTIF_EVENT_TYPES = [
  { value: 'task_assigned', label: 'notifications.prefs.task_assigned' },
  { value: 'status_change', label: 'notifications.prefs.status_change' },
  { value: 'new_comment', label: 'notifications.prefs.new_comment' },
  { value: 'new_ticket', label: 'notifications.prefs.new_ticket' },
  { value: 'ticket_update', label: 'notifications.prefs.ticket_update' },
  { value: 'file_uploaded', label: 'notifications.prefs.file_uploaded' },
  { value: 'invoice_generated', label: 'notifications.prefs.invoice_generated' },
  { value: 'invoice_paid', label: 'notifications.prefs.invoice_paid' },
  { value: 'deposit_invoice', label: 'notifications.prefs.deposit_invoice' },
  { value: 'quote_accepted', label: 'notifications.prefs.quote_accepted' },
  { value: 'time_approved', label: 'notifications.prefs.time_approved' },
  { value: 'member_added', label: 'notifications.prefs.member_added' },
];

function NotificationPreferences() {
  const { t } = useTranslation();
  const { data: prefsData, isLoading } = useNotificationPreferences();
  const updatePrefs = useUpdateNotificationPreferences();
  const preferences = prefsData?.data || [];

  // Convert API data (id, userId, type, email, inApp) to a mutable local map
  const [localPrefs, setLocalPrefs] = useState<Record<string, { email: boolean; inApp: boolean }>>({});

  useEffect(() => {
    if (preferences.length > 0) {
      const map: Record<string, { email: boolean; inApp: boolean }> = {};
      preferences.forEach((p: any) => {
        map[p.type] = { email: p.email, inApp: p.inApp };
      });
      setLocalPrefs(map);
    }
  }, [preferences]);

  const hasChanges = NOTIF_EVENT_TYPES.some(({ value }) => {
    const pref = preferences.find((p: any) => p.type === value);
    const local = localPrefs[value];
    return local && pref && (local.email !== pref.email || local.inApp !== pref.inApp);
  });

  const handleSave = async () => {
    try {
      await updatePrefs.mutateAsync(
        NOTIF_EVENT_TYPES.map(({ value }) => ({
          type: value,
          email: localPrefs[value]?.email ?? true,
          inApp: localPrefs[value]?.inApp ?? true,
        })),
      );
      toast({ title: t('notifications.prefsSaved'), variant: 'success' });
    } catch (err: any) {
      toast({
        title: t('notifications.prefsSaveFailed'),
        description: err?.message || t('errors.unexpected'),
        variant: 'destructive',
      });
    }
  };

  const togglePref = (type: string, field: 'email' | 'inApp') => {
    setLocalPrefs((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: !(prev[type]?.[field] ?? true),
      },
    }));
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bell size={18} className="text-yellow-400" />
          <CardTitle className="text-lg">{t('notifications.prefsTitle')}</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-dyndev-green" />
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-[var(--text-secondary)] mb-4">
              {t('notifications.prefsDesc')}
            </p>

            {/* Table header — hidden on mobile, visible on sm+ */}
            <div className="hidden sm:flex items-center gap-4 px-1 pb-2 border-b border-[var(--border-color)]">
              <span className="flex-1 text-[10px] font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                {t('notifications.prefsType')}
              </span>
              <span className="w-16 text-center text-[10px] font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                {t('notifications.prefsInApp')}
              </span>
              <span className="w-16 text-center text-[10px] font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                {t('notifications.prefsEmail')}
              </span>
            </div>

            {/* Preference rows */}
            <div className="divide-y divide-[var(--border-color)]">
              {NOTIF_EVENT_TYPES.map(({ value, label }) => (
                <div
                  key={value}
                  className="grid grid-cols-1 sm:flex sm:items-center gap-2 sm:gap-4 py-2.5 px-1 rounded-lg hover:bg-[var(--bg-primary)] transition-colors"
                >
                  <span className="text-sm capitalize sm:flex-1 col-span-2">
                    {t(label)}
                  </span>
                  {/* Mobile labels — visible only on small screens */}
                  <div className="flex items-center gap-3 sm:gap-4 sm:w-16 sm:justify-center">
                    <span className="text-[10px] text-[var(--text-secondary)] sm:hidden w-12">{t('notifications.prefsInApp')}</span>
                    <Switch
                      checked={localPrefs[value]?.inApp ?? true}
                      onCheckedChange={() => togglePref(value, 'inApp')}
                      aria-label={`${t('notifications.prefsInApp')} ${value}`}
                    />
                  </div>
                  <div className="flex items-center gap-3 sm:gap-4 sm:w-16 sm:justify-center">
                    <span className="text-[10px] text-[var(--text-secondary)] sm:hidden w-12">{t('notifications.prefsEmail')}</span>
                    <Switch
                      checked={localPrefs[value]?.email ?? true}
                      onCheckedChange={() => togglePref(value, 'email')}
                      aria-label={`${t('notifications.prefsEmail')} ${value}`}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end pt-2">
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!hasChanges || updatePrefs.isPending}
                className="gap-1.5"
              >
                {updatePrefs.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
                {updatePrefs.isPending ? t('notifications.prefsSaving') : t('notifications.prefsSave')}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
